import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getVehiclePath } from '../Utils/vehicleUrls';
import Skeleton from './Skeleton';

const money = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

export function buildBestDealsViewMoreHref(data) {
  const params = new URLSearchParams();
  if (data?.make) params.set('make', data.make);
  if (data?.model) params.set('model', data.model);
  const scope = data?.searchScope || 'nationwide';
  if (data?.originZip) params.set('zip', data.originZip);
  params.set('radius', scope === '250' || scope === '500' ? scope : '9999');
  return `/insights/best-deals?${params.toString()}`;
}

function LoadingCards() {
  return <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid="best-deals-skeletons" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, index) => {
      const visibility = index === 0 ? '' : index === 1 ? 'hidden sm:block' : index === 2 ? 'hidden lg:block' : 'hidden xl:block';
      return <article key={index} data-testid="best-deals-skeleton-card" className={`${visibility} overflow-hidden rounded-xl border border-slate-200 bg-slate-50`}>
        <Skeleton className="aspect-[4/3] w-full rounded-none" />
        <div className="space-y-2 p-3">
          <Skeleton className="h-4 w-5/6" /><Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-6 w-1/2" /><Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-8 w-full rounded-lg" /><Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </article>;
    })}
  </div>;
}

export default function BestDealsNearby({ carId }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [requestCarId, setRequestCarId] = useState(carId);
  const trackedCarId = useRef(null);

  useEffect(() => {
    if (!carId) return undefined;
    const controller = new AbortController();
    setData(null);
    setState('loading');
    setRequestCarId(carId);
    fetch(`${process.env.REACT_APP_API_BASE}/cars/${carId}/best-deals-nearby`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((response) => {
        if (!controller.signal.aborted) { setData(response); setState('success'); }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') { setData(null); setState('error'); }
      });
    return () => controller.abort();
  }, [carId]);

  const results = useMemo(() => Array.isArray(data?.results) ? data.results : [], [data]);
  useEffect(() => {
    if (!carId || requestCarId !== carId || results.length === 0 || trackedCarId.current === carId) return;
    trackedCarId.current = carId;
    window.gtag?.('event', 'best_deals_nearby_view', { source_car_id: Number(carId), result_count: results.length, search_scope: data.searchScope });
  }, [carId, data, requestCarId, results.length]);

  const isLoading = state === 'loading' || requestCarId !== carId;
  if (!isLoading && results.length === 0) return null;
  if (isLoading) return <section data-testid="best-deals-nearby" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-busy="true">
    <h2 className="text-xl font-semibold text-slate-950">Best Deals Nearby</h2>
    <p className="mt-1 text-sm text-slate-600">Finding similar vehicles that appear underpriced.</p>
    <span className="sr-only" role="status">Loading Best Deals Nearby</span><LoadingCards />
  </section>;

  const isNearby = data.searchScope === '250';
  const heading = isNearby ? 'Best Deals Nearby' : `Best Deals on Similar ${data.make || ''} ${data.model || ''}`.replace(/\s+/g, ' ').trim();
  const supportingText = isNearby ? `Similar ${data.make} ${data.model} vehicles that appear underpriced.` : data.searchScope === '500' ? 'Best deals found within 500 miles.' : 'Best deals found nationwide.';
  const href = buildBestDealsViewMoreHref(data);
  return <section data-testid="best-deals-nearby" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-xl font-semibold text-slate-950">{heading}</h2><p className="mt-1 text-sm text-slate-600">{supportingText}</p></div>
      <Link to={href} className="text-sm font-semibold text-blue-700 hover:text-blue-800" onClick={() => window.gtag?.('event', 'best_deals_nearby_view_all', { source_car_id: Number(carId), search_scope: data.searchScope })}>View More Best Deals &rarr;</Link></div>
    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">{results.map((deal, index) => <Link key={deal.id} to={getVehiclePath(deal)} className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50 no-underline transition hover:-translate-y-0.5 hover:shadow-md" onClick={() => window.gtag?.('event', 'best_deals_nearby_vehicle_click', { source_car_id: Number(carId), target_car_id: deal.id, result_position: index + 1, same_trim: Boolean(deal.sameTrim), search_scope: data.searchScope })}>
      <img src={deal.imageUrl} alt={`${deal.year} ${deal.make} ${deal.model}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
      <div className="space-y-2 p-3"><div className="line-clamp-2 text-sm font-semibold text-slate-950">{[deal.year, deal.make, deal.model, deal.trim].filter(Boolean).join(' ')}</div><div className="text-lg font-semibold text-slate-950">{money(deal.price)}</div><div className="text-xs text-slate-600">{deal.mileage == null ? 'Mileage unavailable' : `${Number(deal.mileage).toLocaleString()} miles`}</div><div className="rounded-lg bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-700">{money(deal.savingsAmount)} below {money(deal.mestimate)} estimate</div><div className="line-clamp-2 text-xs text-slate-500">{[deal.dealerName, deal.dealerCity, deal.dealerState].filter(Boolean).join(' | ')}</div></div>
    </Link>)}</div>
  </section>;
}
